import { useMutation, useQuery } from "@apollo/client";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import SaleModel from "../../../../utilities/data/model/Sale";
import {
  MUTATION_CANCEL_SALE,
  MUTATION_REFUND_SALE,
  MUTATION_TRANSFER_WEB_SALE,
  MUTATION_VOID_SALE,
  QUERY_GENERATE_GET_SALE_STRING,
  QUERY_SALE,
  QUERY_SALE_TRANSFER_STRING,
} from "./constants";
import { Sale } from "@towersystems/roam-common/lib/generated-types";
import { Model } from "@nozbe/watermelondb";
import SaleItem from "../../../../utilities/data/model/SaleItem";
import { v4 as uuid } from "uuid";
import { useCallback, useContext } from "react";
import { SaleContext } from "./context";
import Product from "../../../../utilities/data/model/Product";
import { SyncState } from "../../../../utilities/data/constants";
import React from "react";

export const useQuerySale = ({ saleId }: { saleId: string }) => {
  const { loading, error, data, refetch } = useQuery<{
    sale: Sale;
  }>(QUERY_SALE, { variables: { id: saleId } });

  return {
    item: data?.sale,
    loading,
    error,
    refetch,
  };
};

export const useMutationSaleTransferString = () => {
  const [m] = useMutation<{ generateGetSaleString: string }>(
    QUERY_GENERATE_GET_SALE_STRING
  );

  const mutation = React.useCallback(async (saleId: string) => {
    return m({
      variables: {
        saleId,
      },
    }).then((result) => {
      if (!result.data?.generateGetSaleString) {
        throw new Error("No result");
      }
      return result.data?.generateGetSaleString;
    });
  }, []);

  return {
    mutation,
  };
};

export const useVoidSale = () => {
  const [voidSaleMutationFn] = useMutation<{ voidSale: Sale }>(
    MUTATION_VOID_SALE
  );
  const voidSale = useCallback(async (saleId: string): Promise<Sale> => {
    const { data } = await voidSaleMutationFn({
      variables: { id: saleId },
    });
    if (!data?.voidSale) {
      throw new Error("error");
    }
    return data?.voidSale;
  }, []);
  return { voidSale };
};

export const useTransferSale = () => {
  const [transferSaleMutationFn] = useMutation<{ transferWebSale: Sale }>(
    MUTATION_TRANSFER_WEB_SALE
  );

  const transferWebSale = useCallback(async (saleId: string): Promise<Sale> => {
    const { data } = await transferSaleMutationFn({
      variables: { saleId: saleId },
    });
    if (!data?.transferWebSale) {
      throw new Error("error");
    }
    return data?.transferWebSale;
  }, []);

  return { transferWebSale };
};

export const useCancelSale = () => {
  const [cancelSaleMutationFn] = useMutation<{ cancelSale: Sale }>(
    MUTATION_CANCEL_SALE
  );
  const cancelSale = useCallback(async (saleId: string): Promise<Sale> => {
    const { data } = await cancelSaleMutationFn({
      variables: { id: saleId },
    });
    if (!data?.cancelSale) {
      throw new Error("error");
    }
    return data?.cancelSale;
  }, []);
  return { cancelSale };
};

export const useRefundSale = () => {
  const [refundSaleMutationFn] = useMutation<{ initiateReturnSale: Sale }>(
    MUTATION_REFUND_SALE
  );

  const refundSale = useCallback(async (saleId: string): Promise<Sale> => {
    const { data } = await refundSaleMutationFn({
      variables: { id: saleId },
    });
    if (!data?.initiateReturnSale) {
      throw new Error("Unable to refund sale");
    }
    return data?.initiateReturnSale;
  }, []);

  return { refundSale };
};

export const useEmailSaleReceipt = () => {};

export const hydrateData = (model: SaleModel, saleData: Sale) => {
  model.externalId = saleData.id;
  model.syncState = SyncState.UPLOADED;
  model.saleState = saleData.state as any;
  model.invoiceNumber = saleData.invoiceNumber;
  model.purchaseOrderNumber = saleData.purchaseOrderNumber || null;
  model.expectedPickupDate = saleData.expectedPickupDate || null;
  model.numSyncAttempts = 0;
  model.customer.id = saleData.customerId || null;
  model.isRefund = saleData.isReturn;
  model.location.id = saleData.locationId;
  model.staff.id = saleData.staffId;
  model.terminal.id = saleData.terminalId;
  model.quoteAccepted = saleData.quoteAccepted ?? null;
  model.note = saleData.note || "";
  model.invoiceSequence = saleData.invoiceSequence || null;
  model.giftVoucherCode = saleData.giftVoucherCode || null;
  model.payments = saleData.payments.map((payment: any) => ({
    localId: uuid(),
    externalId: payment.id,
    ...payment,
  }));
};

export const usePersistSale = () => {
  const database = useDatabase();

  const persistSale = async (sale: Sale, saleModel?: SaleModel) => {
    return await database.write(async () => {
      let saleRecord: SaleModel | undefined;
      try {
        saleRecord =
          saleModel ??
          (await database.collections.get<SaleModel>("sales").find(sale.id));

        const items = await (saleRecord?.items.fetch() || []);
        for (const item of items) {
          await item.destroyPermanently();
        }
      } catch (error) {
        console.log("error", error);
      }

      if (saleRecord) {
        saleRecord = await saleRecord.update((newSale) => {
          newSale._raw.id = sale.id;
          hydrateData(newSale, sale);
        });
      } else {
        saleRecord = await database.collections
          .get<SaleModel>("sales")
          .create((newSale) => {
            newSale._raw.id = sale.id;
            hydrateData(newSale, sale);
          });
      }

      const batchItemActions: Model[] = [];

      for (const item of sale.lineItems) {
        const product = await database.collections
          .get<Product>("products")
          .find(item.productId)
          .catch(console.log);

        if (!product) {
          throw new Error("product.not.found");
        }

        batchItemActions.push(
          database.get<SaleItem>("sale_items").prepareCreate((newItem) => {
            newItem._raw.id = item.id;
            newItem.sale.id = sale.id;
            newItem.metadata = item.metadata;
            newItem.product.id = item.productId;
            newItem.quantity = item.quantity;
            newItem.basePrice = item.basePrice;
            newItem.taxRatePercent = item.taxRatePercent;
            newItem.serialNumber = item.serialNumber || null;
            newItem.loyaltyPointEarnRate = product?.loyaltyPointEarnValue || 0;
            newItem.name = item.name || null;
            newItem.customerNote = item.customerNote || null;
            newItem.giftCardNumber = item.giftCardNumber || null;
            newItem.adjustments = item.adjustments.map((i) => {
              return {
                localId: uuid(),
                externalId: i.id,
                ...i,
              };
            });
          })
        );
      }
      await database.batch(batchItemActions);
      return saleRecord;
    });
  };

  return persistSale;
};

export const useSaleContextProvider = () => {
  const context = useContext(SaleContext);
  if (!context) {
    throw new Error("No SaleContext context was provided.");
  }
  return context;
};
